import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import { Box, ClickAwayListener } from '@mui/material';
import type { ValueViewerProps } from 'common/types/scalarSpreadsheet';
import FastValueViewer from 'components/ScalarSpreadsheet/utilities/FastValueViewer';
import { useLayoutContext } from 'context/LayoutContext';
import { useCellAnnotationStore } from 'store/useCellAnnotationStore';
import type { CellAnnotationCapabilities } from './annotationRegistry';
import CellAnnotationBar from './CellAnnotationBar';
import CellAnnotationPopupSheet from './CellAnnotationPopupSheet';
import {
  CellAnnotationIconButton,
  CellAnnotationPopper,
  ICON_COLOR_MAP,
  OriginalViewerWrapper,
  ViewerContainer,
} from './CellAnnotationValueViewer.styles';
import PreFxPopoutSpreadsheet from './PreFxPopoutSpreadsheet';
import type { CellAnnotationCellKey, CellAnnotationType } from './types';
import { parseCellKey } from './types';
import useCellAnnotationMatch from './useCellAnnotationMatch';
import { extractFormatFromCell } from './utils/extractFormatFromCell';

// Host numeric cells can arrive here as JS `number` OR as a numeric string
// (e.g. cap-table totals computed by `totalParser` end up string-typed by the
// time the value-viewer renders). Coerce numeric strings to `number`; treat
// blank / non-numeric values as "no host value" so the icon stays gray.
const toHostValue = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return null;
    }
    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }
  return null;
};

// Replace the annotation_type segment of a cellKey to address a sibling
// annotation slot on the same (parent, field). Returns null if the input
// cellKey is malformed (missing one of the four segments).
const buildSiblingCellKey = (
  cellKey: CellAnnotationCellKey,
  nextType: CellAnnotationType
): CellAnnotationCellKey | null => {
  const parsed = parseCellKey(cellKey);
  if (!parsed) {
    return null;
  }
  return `${parsed.parentType}:${parsed.parentId}:${parsed.fieldAlias}:${nextType}`;
};

// `cell.currency` arrives as either a string code or as a `RowFormat.currency`
// object (depends on whether the cell is in a LedgerTable that passes the
// format object through). Normalize so the popout default works in both cases.
const resolveCurrencyCode = (currency: unknown): string | undefined => {
  if (typeof currency === 'string') {
    return currency;
  }
  if (currency && typeof currency === 'object' && 'code' in currency) {
    const { code } = currency as { code?: unknown };
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
};

// Hover-bar grace period: if the user moves OUT of the cell but BACK into the
// bar within this window, keep the bar open. Empirically ~150ms feels stable
// without becoming sluggish; 200ms gives a comfortable margin.
const HOVER_CLOSE_DELAY_MS = 200;

// Feature gate for the hover annotation-picker bar. Disabled because with
// only one annotation type currently surfaceable (Pre-FX), the floating bar
// reads as noise — users now trigger Pre-FX directly via the in-cell
// MoneyChip click. Leave the bar component + wiring in place: flip this to
// `true` to re-enable once cross_reference (or another type) ships on these
// cells and the picker has more than one option to justify its existence.
const ANNOTATION_BAR_ENABLED = false;

/**
 * CellAnnotationValueViewer
 *
 * Renders inline annotation indicators on a cell, mounts the type-specific
 * popouts, and (when the cell exposes `cellAnnotationBarCapabilities`) the
 * hover-triggered annotation reaction bar that lets users pick a *new*
 * annotation type to attach.
 *
 * Hosts opt cells in by setting three fields on the cell config:
 *   - `cellAnnotation`: the primary annotation slot's config (drives initial
 *     icon visibility before any state hydrates).
 *   - `cellAnnotationCellKey`: the colon-joined parent/field/type cellKey.
 *   - `cellAnnotationBarCapabilities` (optional): when set, the hover bar
 *     surfaces the applicable annotation icons. Money-amount cells get both
 *     Pre-FX and Cross Reference; pure numeric cells get Cross Reference
 *     alone. See `annotationRegistry.ts`.
 *
 * Trigger: pure hover. Hovering the value viewer opens the bar; leaving the
 * cell or the bar (with a small grace timer to allow moving the mouse over
 * the bar itself) closes it. Bar -> popout is a single click; popouts pin
 * open until the user clicks away or hits Escape.
 *
 * Visual indicators in the cell itself:
 *   - Cross Reference: a green/red assignment-icon button stays inline
 *     because the icon's color encodes the match state (sum vs host) and
 *     reading that at a glance is the feature's main value.
 *   - Pre-FX: NO inline icon. The currency chip shown alongside the amount
 *     by `MoneyChipValueViewer` carries the "this cell has a Pre-FX
 *     annotation" signal AND is the click target that re-opens the popout
 *     (hosts wire `cell.onChipClick` to `useCellAnnotationStore.openPopout`).
 *     The hover bar is feature-gated off today (see `ANNOTATION_BAR_ENABLED`)
 *     since Pre-FX is the only enabled annotation on these cells.
 */
const CellAnnotationValueViewer: React.FC<ValueViewerProps> = ({ cell, ...rest }) => {
  // Two anchor sources:
  //   - viewerRef: the value-viewer container <div>. Popouts anchor here so
  //     `placement="bottom-start"` aligns to the cell's left edge.
  //   - barAnchorEl: the host `<td>`, derived by walking up from the viewer
  //     at hover time. The bar uses `placement="top"` so anchoring on the
  //     <td> places it above the *cell*, not above the inner div — otherwise
  //     the bar's translation gets computed against the inner box and ends
  //     up overlapping the cell's text.
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const xrefAnchorRef = useRef<HTMLDivElement | null>(null);
  const [xrefOpen, setXrefOpen] = useState(false);
  const [preFxOpen, setPreFxOpen] = useState(false);
  // Bar state. `barOpen` drives the Popper visibility; `barAnchorEl` snapshots
  // the host <td> at hover-time so the bar pins to the cell that triggered
  // it even if hover state churns elsewhere.
  const [barOpen, setBarOpen] = useState(false);
  const [barAnchorEl, setBarAnchorEl] = useState<HTMLElement | null>(null);
  // Grace timer for the hover-out → hover-in-bar handoff.
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const config = cell.cellAnnotation;
  const cellKey = (cell.cellAnnotationCellKey ?? '') as CellAnnotationCellKey;
  const numericHostValue = toHostValue(cell.value);
  // Bar is feature-gated off (see ANNOTATION_BAR_ENABLED). When disabled the
  // hover handlers and render branch both short-circuit, but the cell's
  // `cellAnnotationBarCapabilities` wiring stays intact so flipping the flag
  // back on requires no host changes.
  const barCapabilities = ANNOTATION_BAR_ENABLED
    ? (cell.cellAnnotationBarCapabilities as CellAnnotationCapabilities | undefined)
    : undefined;

  const { isExportMode } = useLayoutContext();

  // Resolve the sibling annotation cellKey relative to whatever the host
  // wired up. The viewer doesn't care which type was the "primary" config —
  // it renders an icon per annotation_type that has store state.
  const xrefCellKey = useMemo<CellAnnotationCellKey | null>(() => {
    if (!cellKey) {
      return null;
    }
    if (config?.annotationType === 'cross_reference') {
      return cellKey;
    }
    return buildSiblingCellKey(cellKey, 'cross_reference');
  }, [cellKey, config?.annotationType]);
  const preFxCellKey = useMemo<CellAnnotationCellKey | null>(() => {
    if (!cellKey) {
      return null;
    }
    if (config?.annotationType === 'pre_fx_currency') {
      return cellKey;
    }
    return buildSiblingCellKey(cellKey, 'pre_fx_currency');
  }, [cellKey, config?.annotationType]);

  // Subscribe to the cross-ref slot's presence so a sibling annotation
  // hydrated for this parent triggers a re-render once the inline icon
  // should appear.
  const xrefStatePresent = useCellAnnotationStore(s => (xrefCellKey ? Boolean(s.states[xrefCellKey]) : false));

  // Subscribe to external popout-open requests (e.g. inline chip click on a
  // CFL Amount cell). When set, an effect below opens the local Pre-FX
  // popout state; closing the popout resets the flag so a subsequent
  // request fires again.
  const openPopoutRequested = useCellAnnotationStore(s => (cellKey ? s.openPopouts[cellKey] : false) ?? false);
  const closePopoutInStore = useCellAnnotationStore(s => s.closePopout);

  useEffect(() => {
    if (openPopoutRequested) {
      setPreFxOpen(true);
    }
  }, [openPopoutRequested]);

  // On unmount or cellKey change, drop any pending open-request for this cell
  // so a future viewer mounting on the same cellKey starts clean.
  useEffect(
    () => () => {
      if (cellKey) {
        closePopoutInStore(cellKey);
      }
    },
    [cellKey, closePopoutInStore]
  );

  const closePreFx = useCallback(() => {
    setPreFxOpen(false);
    if (cellKey) {
      closePopoutInStore(cellKey);
    }
  }, [cellKey, closePopoutInStore]);

  const xrefColor = useCellAnnotationMatch(xrefCellKey ?? '', numericHostValue);

  // Default source-currency for the Pre-FX popout. Hosts can set
  // `defaultPreFxCurrency` directly; otherwise fall back to a normalized
  // `cell.currency`, finally USD if neither is available.
  const defaultPreFxCurrency = cell.defaultPreFxCurrency ?? resolveCurrencyCode(cell.currency) ?? 'USD';

  const OriginalViewer = cell.originalValueViewer ?? FastValueViewer;

  const cancelCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeBar = useCallback(() => {
    cancelCloseTimer();
    setBarOpen(false);
  }, [cancelCloseTimer]);

  // Open the bar on cell hover. Hover trigger only fires when the host has
  // declared `cellAnnotationBarCapabilities`; cells without it never see the
  // bar (e.g. read-only display rows).
  //
  // Resolve the bar's anchor to the nearest enclosing <td> so the floating
  // bar sits above the entire cell, not above the inner viewer <div> (which
  // is shorter and would let the bar's translation overlap the cell's text).
  // Falls back to the viewer itself when there's no <td> ancestor — happens
  // outside the spreadsheet grid (e.g. unit tests rendering the viewer in a
  // <div>) so the bar still anchors somewhere sensible.
  const handleViewerMouseEnter = useCallback(() => {
    if (!barCapabilities) {
      return;
    }
    cancelCloseTimer();
    const td = viewerRef.current?.closest('td') as HTMLElement | null;
    setBarAnchorEl(td ?? viewerRef.current);
    setBarOpen(true);
  }, [barCapabilities, cancelCloseTimer]);

  // Schedule a delayed close on cell mouse-leave. The bar's own onMouseEnter
  // cancels the timer, allowing the user to glide the cursor from cell -> bar
  // without the bar disappearing. Closing immediately would make the bar
  // unreachable on cells whose viewer is shorter than the bar's vertical
  // offset.
  const handleViewerMouseLeave = useCallback(() => {
    if (!barCapabilities) {
      return;
    }
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setBarOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, [barCapabilities, cancelCloseTimer]);

  // Bar hover handlers — keep the bar open while the cursor is inside it.
  const handleBarMouseEnter = useCallback(() => {
    cancelCloseTimer();
  }, [cancelCloseTimer]);
  const handleBarMouseLeave = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setBarOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY_MS);
  }, []);

  // Cleanup the timer on unmount so a quick navigate-away doesn't fire a
  // close on a stale component.
  useEffect(() => () => cancelCloseTimer(), [cancelCloseTimer]);

  const handleBarSelect = useCallback((type: CellAnnotationType) => {
    // The bar's auto-close fires after onSelectType. Open the matching
    // popout immediately so the user goes from "hover -> click icon" to
    // "popout open and editable" without an intermediate state.
    if (type === 'pre_fx_currency') {
      setPreFxOpen(true);
    } else if (type === 'cross_reference') {
      setXrefOpen(true);
    }
  }, []);

  const toggleXref = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setXrefOpen(prev => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setXrefOpen(false);
        closePreFx();
        closeBar();
      }
    },
    [closeBar, closePreFx]
  );

  // Inherited format is stable for a given cell (number/decimal-format-wise)
  // but cell.value changes per render. Memoize on the format-shaping fields
  // only so the popup doesn't see a new prop identity on every host re-render.
  const inheritedFormat = useMemo(
    () =>
      extractFormatFromCell({
        gridType: cell.gridType,
        format: cell.format,
        currency: cell.currency,
        dbDecimalPlaces: cell.dbDecimalPlaces,
        maxDecimalDigits: cell.maxDecimalDigits,
      }),
    [cell.gridType, cell.format, cell.currency, cell.dbDecimalPlaces, cell.maxDecimalDigits]
  );

  // If the cell hasn't opted in (or is missing its cellKey), behave exactly
  // like the wrapped original. Without a cellKey we can't address the store.
  if (!config || !cellKey || isExportMode) {
    return <OriginalViewer cell={cell} {...rest} />;
  }

  const showXrefIcon = config.annotationType === 'cross_reference' || xrefStatePresent;
  const xrefIconFill = ICON_COLOR_MAP[xrefColor];

  return (
    <ViewerContainer
      ref={viewerRef}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleViewerMouseEnter}
      onMouseLeave={handleViewerMouseLeave}>
      <OriginalViewerWrapper>
        <OriginalViewer cell={cell} {...rest} />
      </OriginalViewerWrapper>
      {showXrefIcon && xrefCellKey && (
        <Box ref={xrefAnchorRef} sx={{ display: 'inline-flex' }}>
          <CellAnnotationIconButton
            size="small"
            iconColor={xrefIconFill}
            onMouseDown={event => event.stopPropagation()}
            onClick={toggleXref}
            aria-label="Open cell annotation"
            data-testid={`cell-annotation-toggle-${xrefCellKey}`}>
            {xrefColor === 'red' ? (
              <AssignmentLateIcon fontSize="small" style={{ fill: xrefIconFill }} />
            ) : (
              <AssignmentTurnedInIcon fontSize="small" style={{ fill: xrefIconFill }} />
            )}
          </CellAnnotationIconButton>
        </Box>
      )}
      {/*
        Cross-reference popper. Anchors on the viewer container so the popup
        aligns with the cell's left edge regardless of where the inline icon
        ends up rendering.
      */}
      {xrefCellKey && showXrefIcon && (
        <CellAnnotationPopper open={xrefOpen} anchorEl={viewerRef.current} placement="bottom-start">
          <ClickAwayListener
            onClickAway={event => {
              // Synthetic mouse-down dispatched by the popup's '=' lock-in (see
              // handlePopupKeyDown in CellAnnotationPopupSheet) fires with
              // detail === 0; ignore those so the popup stays open after
              // entering pick-target mode.
              if ((event as MouseEvent).detail === 0) return;
              setXrefOpen(false);
            }}>
            <Box>
              {xrefOpen && (
                <CellAnnotationPopupSheet
                  cellKey={xrefCellKey}
                  config={
                    config.annotationType === 'cross_reference'
                      ? config
                      : { annotationType: 'cross_reference', rowMode: 'single_cell', columns: [] }
                  }
                  inheritedFormat={inheritedFormat}
                />
              )}
            </Box>
          </ClickAwayListener>
        </CellAnnotationPopper>
      )}
      {preFxCellKey && (
        <PreFxPopoutSpreadsheet
          open={preFxOpen}
          anchorEl={viewerRef.current}
          cellKey={preFxCellKey}
          defaultCurrency={defaultPreFxCurrency}
          hostCurrency={cell.preFxHostCurrency}
          transactionDate={cell.preFxTransactionDate}
          onHostPreview={cell.onPreFxHostPreview}
          onClose={closePreFx}
        />
      )}
      {barCapabilities && (
        <CellAnnotationBar
          open={barOpen}
          anchorEl={barAnchorEl}
          capabilities={barCapabilities}
          onClose={closeBar}
          onSelectType={handleBarSelect}
          onMouseEnter={handleBarMouseEnter}
          onMouseLeave={handleBarMouseLeave}
        />
      )}
    </ViewerContainer>
  );
};

export default CellAnnotationValueViewer;
